"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

const OurIndustryPartners = ({ course }) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Normalize course data: handle Strapi response, array, or single object
  let courseData = null;

  if (Array.isArray(course)) {
    // Case 1: [course, ...]
    courseData = course[0] || null;
  } else if (course?.data && Array.isArray(course.data)) {
    // Case 2: { data: [course, ...], meta: {...} }
    courseData = course.data[0] || null;
  } else {
    // Case 3: course is already a single course object
    courseData = course || null;
  }

  const industryPartners = courseData?.OurIndustryPartners || [];

  if (!industryPartners.length) {
    return null;
  }

  const getImageSrc = (img) => {
    if (!img) return "";
    return img.formats?.small?.url || img.url || "";
  };

  const getAltText = (img) => {
    if (!img) return "Industry Partner Logo";
    return img.alternativeText || img.name || "Industry Partner Logo";
  };

  // State to track screen size for responsive button visibility
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // Limit to 3 rows on all devices
  // Desktop: 18 items (3 rows × 6 cols), Tablet: 15 items (5 rows × 3 cols), Mobile: 6 items (3 rows × 2 cols)
  const displayLimit = isMobile ? 6 : isTablet ? 15 : 18;
  const displayedPartners = industryPartners.slice(0, displayLimit);

  // Show button based on screen size:
  // Desktop: show if > 18, Tablet: show if > 15, Mobile: show if > 6
  const hasMore = isMobile
    ? industryPartners.length > 6
    : isTablet
      ? industryPartners.length > 15
      : industryPartners.length > 18;

  return (
    <section className="page-container">
      <div className="pb-[50px] mt-[20px] md:pb-[80px] lg:pb-[100px]">
        <h2 className="mb-[12px] text-black">Our Industry Partners</h2>
        <p className="text-black w-full md:w-[80%] xl:w-[60%] mb-[30px] md:mb-[40px]">
          We collaborate with leading organisations across sectors to support
          practical learning, industry exposure, and career readiness for our
          students
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {displayedPartners.map((partner, index) => {
            const src = getImageSrc(partner);
            const alt = getAltText(partner);

            if (!src) return null;

            return (
              <div
                key={partner.documentId || partner.id || index}
                className="flex items-center justify-center"
              >
                {/* Image Wrapper */}
                <div
                  className="
                        w-[213px]
                        h-[102px]
                        border
                        border-gray-300
                        flex
                        items-center
                        justify-center
                        bg-white
                        p-2
                    "
                >
                  <Image
                    src={src}
                    alt={alt}
                    width={213}
                    height={102}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button - Show only if more than 9 logos */}
        {hasMore && (
          <div className="w-full flex justify-center mt-10">
            <Link
              href=""
              className="cursor-pointer flex items-center gap-2 text-[16px] font-semibold text-black hover:text-[#0475B3] transition-colors"
              onClick={(e) => {
                e.preventDefault();
                setModalOpen(true);
              }}
            >
              <span className="text-lg">View All Partners</span>
              <div className="bg-[#0475B3] hover:bg-[#FFA201] text-lg p-1 transition flex items-center justify-center">
                <ArrowRight className="text-white" />
              </div>
            </Link>
          </div>
        )}

        {/* Modal */}
        <IndustryPartnersModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          partners={industryPartners}
          getImageSrc={getImageSrc}
          getAltText={getAltText}
        />
      </div>
    </section>
  );
};

// Modal Component
function IndustryPartnersModal({
  open,
  onClose,
  partners,
  getImageSrc,
  getAltText,
}) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  // Mount/unmount with exit animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      // next tick so transitions apply
      requestAnimationFrame(() => setShow(true));
    } else {
      setShow(false);
      const t = setTimeout(() => setMounted(false), 400); // match duration
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on ESC + lock scroll only when mounted
  useEffect(() => {
    if (!mounted) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* overlay */}
      <button
        aria-label="Close modal"
        onClick={onClose}
        className={[
          "absolute inset-0 bg-black/60",
          show ? "opacity-100" : "opacity-0",
        ].join(" ")}
        style={{
          transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        type="button"
      />

      {/* panel wrapper (centering) */}
      <div className="relative w-full h-full flex items-center justify-center px-3">
        {/* modal panel */}
        <div
          className={[
            "relative w-[85%] max-w-6xl bg-white rounded-2xl shadow-xl overflow-hidden",
            "will-change-transform will-change-opacity",
            show
              ? "opacity-100 translate-y-0 scale-100"
              : "opacity-0 translate-y-10 scale-90",
          ].join(" ")}
          style={{
            transition:
              "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1), transform 400ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="All industry partners"
        >
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900">
                All Industry Partners
              </h3>
              <p className="text-sm text-slate-600">
                Organizations collaborating with us for student success
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm"
            >
              Close
            </button>
          </div>

          <div className="p-4 md:p-6 max-h-[70vh] overflow-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {partners.map((partner, idx) => {
                const src = getImageSrc(partner);
                const alt = getAltText(partner);

                if (!src) return null;

                return (
                  <div
                    key={`${partner.documentId || partner.id || idx}`}
                    className="bg-white border border-gray-300 rounded-lg flex items-center justify-center p-2"
                    style={{ minHeight: 102 }}
                  >
                    <div className="relative w-full h-20">
                      <Image
                        src={src}
                        alt={alt}
                        fill
                        sizes="(max-width: 640px) 120px, (max-width: 1024px) 160px, 213px"
                        className="object-contain"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-4 md:px-6 py-4 border-t flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OurIndustryPartners;
